import { useState, useEffect } from "react";
import { toast } from "sonner";
import { submitConsultation } from "@/lib/api";
import { track } from "@/lib/analytics";
import { TID, CONSULTATION, CONSULTATION_TOPICS } from "@/constants/testIds";
import { Check, Video, CalendarPlus, ExternalLink } from "lucide-react";

/**
 * ConsultationForm — direct booking for a 30-min online consultation with the
 * sales team. On successful submission, the API returns a Google Calendar
 * template URL the visitor can add straight to their own calendar.
 */
function nextRoundedSlot() {
  // Suggest the next half-hour slot ≥ tomorrow 10:00 IST as a friendly default
  const now = new Date();
  const suggested = new Date(now);
  suggested.setDate(suggested.getDate() + 1);
  suggested.setHours(10, 0, 0, 0);
  // Format for datetime-local (YYYY-MM-DDTHH:MM)
  const pad = (n) => String(n).padStart(2, "0");
  return `${suggested.getFullYear()}-${pad(suggested.getMonth() + 1)}-${pad(suggested.getDate())}T${pad(suggested.getHours())}:${pad(suggested.getMinutes())}`;
}

export default function ConsultationForm({ source = "consultation_page", ctaLabel = CONSULTATION.cta, compact = false }) {
  const [values, setValues] = useState({
    name: "",
    mobile: "",
    email: "",
    city: "",
    preferred_datetime: nextRoundedSlot(),
    topic: CONSULTATION_TOPICS[0],
    notes: "",
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (result?.google_calendar_url) {
      // Auto-open Google Calendar tab so the visitor gets the "add" screen immediately
      window.open(result.google_calendar_url, "_blank", "noopener,noreferrer");
    }
  }, [result]);

  const set = (k) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setValues((v) => ({ ...v, [k]: val }));
  };

  useEffect(() => {
    track("form_open", { form: "consultation", source });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!values.consent) {
      toast.error("Please tick the consent box to book your call.");
      return;
    }
    // Convert datetime-local to ISO with IST offset (+05:30)
    const isoWithTZ = values.preferred_datetime.length === 16
      ? `${values.preferred_datetime}:00+05:30`
      : values.preferred_datetime;
    setSubmitting(true);
    try {
      const res = await submitConsultation({
        ...values,
        preferred_datetime: isoWithTZ,
        duration_minutes: CONSULTATION.duration,
        source,
      });
      setResult(res);
      track("form_submit", { form: "consultation", source, email: values.email, mobile: values.mobile, name: values.name });
      toast.success("Consultation booked. Opening Google Calendar…");
    } catch (err) {
      track("form_error", { form: "consultation", source });
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    const when = new Date(result.preferred_datetime);
    return (
      <div className="border border-forest/20 rounded-xl p-6 md:p-8 bg-white space-y-4">
        <div className="w-12 h-12 rounded-full bg-lime grid place-items-center">
          <Check className="text-void" size={22} />
        </div>
        <div>
          <div className="eyebrow text-teal">Consultation booked</div>
          <div className="font-display text-forest text-2xl md:text-3xl mt-2 leading-tight">
            See you on {when.toLocaleString("en-IN", { weekday: "long", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}.
          </div>
          <div className="text-sm text-forest/70 mt-2 leading-relaxed">
            A senior advisor from the Pali Hill City sales team will send the
            Google Meet link 30 minutes before the call. Confirmation ID{" "}
            <span className="text-forest font-semibold">{result.id.slice(0, 8).toUpperCase()}</span>.
          </div>
        </div>
        <div className="flex flex-wrap gap-3 pt-2">
          <a
            data-testid={TID.consultGcalBtn}
            href={result.google_calendar_url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary text-sm py-2.5"
          >
            <CalendarPlus size={16} /> Add to Google Calendar
            <ExternalLink size={13} className="opacity-60" />
          </a>
          <a
            href="https://wa.me/918446448123?text=Hi%2C%20I%27ve%20just%20booked%20an%20online%20consultation."
            target="_blank"
            rel="noreferrer"
            className="btn-outline-forest text-sm py-2.5"
          >
            WhatsApp us
          </a>
        </div>
        <p className="text-[11px] text-forest/50 pt-2 border-t border-forest/10">
          The Google Calendar tab opens automatically. If it was blocked by your
          browser, use the button above.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={compact ? "space-y-3" : "space-y-4"}>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Name" required>
          <input data-testid={TID.consultName} required value={values.name} onChange={set("name")} className={inputCls} />
        </Field>
        <Field label="Mobile" required>
          <input data-testid={TID.consultMobile} required inputMode="tel" value={values.mobile} onChange={set("mobile")} className={inputCls} />
        </Field>
        <Field label="Email" required>
          <input data-testid={TID.consultEmail} required type="email" value={values.email} onChange={set("email")} className={inputCls} />
        </Field>
        <Field label="City">
          <input data-testid={TID.consultCity} value={values.city} onChange={set("city")} className={inputCls} />
        </Field>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Preferred date & time (IST)" required>
          <input
            data-testid={TID.consultDateTime}
            required
            type="datetime-local"
            value={values.preferred_datetime}
            onChange={set("preferred_datetime")}
            className={inputCls}
          />
        </Field>
        <Field label="Topic" required>
          <select data-testid={TID.consultTopic} value={values.topic} onChange={set("topic")} className={selectCls}>
            {CONSULTATION_TOPICS.map((t) => <option key={t}>{t}</option>)}
          </select>
        </Field>
      </div>
      {!compact && (
        <Field label="Anything specific you'd like us to prepare?">
          <textarea
            data-testid={TID.consultNotes}
            rows={2}
            value={values.notes}
            onChange={set("notes")}
            className={inputCls + " resize-none"}
            placeholder="Budget, family situation, RERA questions…"
          />
        </Field>
      )}

      <label className="flex items-start gap-3 pt-2">
        <input
          data-testid={TID.consultConsent}
          type="checkbox"
          required
          checked={values.consent}
          onChange={set("consent")}
          className="mt-1 w-4 h-4 accent-[color:var(--phc-forest)]"
        />
        <span className="text-xs text-forest/80 leading-relaxed">
          I consent to being contacted via WhatsApp, Call, and SMS to confirm my
          consultation. (TRAI regulations.)
        </span>
      </label>

      <button
        data-testid={TID.consultSubmit}
        disabled={submitting}
        className="btn-primary w-full sm:w-auto"
      >
        <Video size={16} />
        {submitting ? "Booking…" : ctaLabel}
      </button>

      <p className="text-[11px] text-forest/50 pt-1">
        Free · 30-min Google Meet · Instantly added to your Google Calendar.
      </p>
    </form>
  );
}

const inputCls =
  "w-full border-0 border-b border-forest/25 bg-transparent py-3 px-0 text-forest placeholder:text-forest/40 focus:outline-none focus:border-forest transition-colors text-[15px] font-body";
const selectCls = inputCls + " appearance-none pr-6";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="eyebrow text-forest/60 block mb-1">
        {label} {required && <span className="text-teal">*</span>}
      </span>
      {children}
    </label>
  );
}
